import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import apiClient from '../../requests/api/apiClient';
import { ReduxState } from '../../types';
import { FetchError } from '../../views/types';
import { DASHBOARD_CHARTS } from '../../utils/dashboardChartMapping';
import { UserRole, UserType } from '../../constants/user_types';

interface BiweeklyReport {
  id: string;
  startDate: string;
  endDate: string;
  createdBy: string;
  createdAt: string;
  selectedCharts?: string[];
}

interface BiweeklyReportModalProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit' | 'view';
  reportData?: BiweeklyReport | null;
  sectorId: string;
  onReportSaved: () => void;
}

const BiweeklyReportModal: React.FC<BiweeklyReportModalProps> = ({
  open,
  onClose,
  mode,
  reportData,
  sectorId,
  onReportSaved,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { selectedAccount } = useSelector((state: ReduxState) => state.userAccount);
  const currentUser = useSelector((state: ReduxState) => state.account.user);

  const isAdmin = currentUser?.role === UserRole.ADMIN && currentUser?.type === UserType.CDTEC;
  const isOperator = currentUser?.role === UserRole.OPERATOR
     && currentUser?.type === UserType.CDTEC;
  const isAgro = currentUser?.role === UserRole.AGRO && currentUser?.type === UserType.CDTEC;

  const canEdit = isAdmin || isOperator || isAgro;

  const [formData, setFormData] = useState({
    startDate: '',
    endDate: '',
  });

  const [selectedDashboard, setSelectedDashboard] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (reportData && mode !== 'create') {
      setFormData({
        startDate: dayjs(reportData.startDate).utc().format('YYYY-MM-DD'),
        endDate: dayjs(reportData.endDate).utc().format('YYYY-MM-DD'),
      });
    } else {
      setFormData({
        startDate: '',
        endDate: '',
      });
      setSelectedDashboard('');
    }
  }, [reportData, mode, open]);

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    if (mode === 'view') return;
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleDashboardChange = (event: any) => {
    if (mode === 'view') return;
    setSelectedDashboard(event.target.value);
  };

  // Calcular fecha máxima para fecha de fin (15 días después de fecha inicio)
  const getMaxEndDate = () => {
    if (!formData.startDate) return undefined;
    const startDate = dayjs(formData.startDate).utc();
    const maxEndDate = startDate.add(15, 'day');
    return maxEndDate.format('YYYY-MM-DD');
  };

  // Calcular fecha mínima para fecha de inicio (15 días antes de fecha fin)
  const getMinStartDate = () => {
    if (!formData.endDate) return undefined;
    const endDate = dayjs(formData.endDate).utc();
    const minStartDate = endDate.subtract(15, 'day');
    return minStartDate.format('YYYY-MM-DD');
  };

  const handleSubmit = async () => {
    try {
      if (!formData.startDate || !formData.endDate) {
        enqueueSnackbar('Por favor complete todas las fechas', { variant: 'error' });
        return;
      }

      const startDate = dayjs(formData.startDate).utc();
      const endDate = dayjs(formData.endDate).utc();

      if (startDate.isSameOrAfter(endDate)) {
        enqueueSnackbar('La fecha de inicio debe ser anterior a la fecha de fin', { variant: 'error' });
        return;
      }

      // Calcular la diferencia en días
      const diffDays = endDate.diff(startDate, 'day');

      if (diffDays > 15) {
        enqueueSnackbar('El período no puede ser mayor a 15 días', { variant: 'error' });
        return;
      }

      setIsSubmitting(true);

      const requestData = {
        sectorId,
        startDate: dayjs(formData.startDate).format('YYYY-MM-DD'),
        endDate: dayjs(formData.endDate).format('YYYY-MM-DD'),
        createdBy: selectedAccount?.name || 'Usuario',
        ...(selectedDashboard && {
          selectedCharts: DASHBOARD_CHARTS[selectedDashboard as keyof typeof DASHBOARD_CHARTS],
        }),
      };

      if (mode === 'create') {
        await apiClient.biweeklyReport.biweeklyReportCreate(requestData);
        enqueueSnackbar('Informe quincenal creado exitosamente', { variant: 'success' });
      } else if (mode === 'edit' && reportData) {
        await apiClient.biweeklyReport.biweeklyReportUpdate(reportData.id, {
          startDate: dayjs(formData.startDate).format('YYYY-MM-DD'),
          endDate: dayjs(formData.endDate).format('YYYY-MM-DD'),
        });
        enqueueSnackbar('Informe quincenal actualizado exitosamente', { variant: 'success' });
      }

      onReportSaved();
    } catch (err) {
      const { error } = err as FetchError;
      enqueueSnackbar(error.message, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getModalTitle = () => {
    switch (mode) {
      case 'create':
        return 'Crear Informe Quincenal';
      case 'view':
        return 'Ver Informe Quincenal';
      case 'edit':
        return 'Editar Informe Quincenal';
      default:
        return 'Informe Quincenal';
    }
  };

  const getSubmitButtonText = () => {
    if (isSubmitting) return 'Guardando...';
    return mode === 'edit' ? 'Actualizar' : 'Crear';
  };

  const isFieldEditable = mode !== 'view' && canEdit;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          background: '#f5f7fa',
          borderBottom: 'none',
          paddingY: 2,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 'bold',
              display: 'inline-block',
              position: 'relative',
              marginBottom: 0.5,
            }}
          >
            {getModalTitle()}
            <Box
              sx={{
                height: 3,
                width: '100%',
                background: '#4285f4',
                position: 'absolute',
                left: 0,
                bottom: -6,
                borderRadius: 2,
              }}
            />
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ paddingTop: 8, paddingX: 3 }}>
        <Grid container spacing={2} sx={{ marginTop: 2 }}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Fecha de inicio"
              type="date"
              value={formData.startDate}
              onChange={handleInputChange('startDate')}
              InputLabelProps={{ shrink: true }}
              disabled={!isFieldEditable}
              required
              inputProps={{
                max: getMinStartDate(),
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Fecha de fin"
              type="date"
              value={formData.endDate}
              onChange={handleInputChange('endDate')}
              InputLabelProps={{ shrink: true }}
              disabled={!isFieldEditable}
              required
              inputProps={{
                min: formData.startDate,
                max: getMaxEndDate(),
              }}
            />
          </Grid>
          {(mode === 'create' || mode === 'edit') && (
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                * Las fechas están limitadas a un período máximo de 15 días
              </Typography>
            </Grid>
          )}
          {mode === 'create' && (
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Visualización (opcional)</InputLabel>
                <Select
                  value={selectedDashboard}
                  onChange={handleDashboardChange}
                  label="Visualización (opcional)"
                  disabled={!isFieldEditable}
                >
                  <MenuItem value="">Ninguno</MenuItem>
                  <MenuItem value="soil">Suelo</MenuItem>
                  <MenuItem value="plant">Planta</MenuItem>
                </Select>
              </FormControl>
              {selectedDashboard && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Se seleccionarán automáticamente los gráficos de la visualización: {selectedDashboard === 'soil' ? 'Suelo' : 'Planta'}
                </Typography>
              )}
            </Grid>
          )}
          {mode === 'view' && reportData && (
            <>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Creado por"
                  value={reportData.createdBy}
                  disabled
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Fecha de creación"
                  value={new Date(reportData.createdAt).toLocaleDateString('es-ES')}
                  disabled
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {mode === 'view' ? 'Cerrar' : 'Cancelar'}
        </Button>
        {mode !== 'view' && (
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={isSubmitting}
          >
            {getSubmitButtonText()}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BiweeklyReportModal;
